import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import WorkspaceProvider from '../WorkspaceProvider';
import { AdminConfigProvider, useAdminConfig } from '../AdminConfigProvider';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { WorkspacePausedBanner } from './WorkspacePausedBanner';
import { ProviderStatusBanner } from './ProviderStatusBanner';
import { PageEnter } from '../ui/PageEnter';
import { TopupPickerModal } from '../billing/TopupPickerModal';
import { FeatureNotAvailableModal } from '../billing/FeatureNotAvailableModal';
import { ProAiVisibilityLaunchedNotice } from '../notices/OneTimeNotice';
import { BetaFeedbackWidget } from '../feedback/BetaFeedbackWidget';
import { ChatPanel } from '../chat/ChatPanel';
import { inviteLinkApi } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useMe, useBillingStatus } from '@/hooks';
import { useWorkspaceContext } from '../WorkspaceProvider';

/**
 * Authenticated dashboard shell. Nested under <AuthGuard />.
 *
 * Responsibilities:
 *   - Wrap in <WorkspaceProvider> so every descendant can read active workspace
 *   - Mount <Sidebar> (floating dock) + <Topbar> (full-width glass bar)
 *   - Hydrate the auth store with /api/auth/me on mount
 *   - Fade + 8px page transition on every route change
 *   - Mobile: hamburger toggles a sidebar drawer
 */
export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // V152 — Merkflow AI chat panel. State lives here so the panel
  // persists across route changes (the panel is a portal/overlay,
  // not a route).
  const [chatOpen, setChatOpen] = useState(false);
  const { data: meData } = useMe();
  const setUser = useAuthStore((s) => s.setUser);
  const { activeWorkspace, workspaces, isLoading: workspacesLoading } = useWorkspaceContext();
  const { data: billing } = useBillingStatus(activeWorkspace?.id ?? '');

  const { i18n } = useTranslation();

  // Defence-in-depth: a user who clears localStorage mid-onboarding can
  // navigate directly to /dashboard with onboardingCompleted=false. The
  // backend rejects most workspace API calls in that state, so the
  // dashboard would render empty rather than broken — but it's sloppy.
  // Same idea for paused workspaces: redirect to the upgrade prompt
  // instead of letting them poke around an inert dashboard.
  //
  // Skipped while `meData` is still loading (avoids a redirect loop on
  // the first paint).
  useEffect(() => {
    if (!meData) return;
    if (meData.onboardingCompleted === false) {
      if (!location.pathname.startsWith('/onboarding')) {
        navigate('/onboarding', { replace: true });
      }
      return;
    }
    // Heal users who landed in the email-password + beta-invite
    // deadlock: register set `onboardingCompleted=true` but the wizard
    // (which creates the workspace) was skipped, so they reach the
    // dashboard with zero workspaces. Bounce back to the wizard so
    // they can name one and unblock everything downstream
    // (brand creation, connections, beta-invite redemption).
    if (!workspacesLoading && workspaces.length === 0) {
      if (!location.pathname.startsWith('/onboarding')) {
        navigate('/onboarding', { replace: true });
      }
      return;
    }
    // Waitlisted account (gate-closed self-signup, not yet activated):
    // onboarding is allowed to complete above, but once done the user is
    // parked on the waiting-list screen until an admin flips them to active.
    if (meData.accessStatus === 'waitlisted' && location.pathname !== '/waitlisted') {
      navigate('/waitlisted', { replace: true });
      return;
    }
    if (billing?.plan === 'paused' && location.pathname !== '/suspended') {
      navigate('/suspended', { replace: true });
    }
  }, [meData, billing, location.pathname, navigate, workspaces, workspacesLoading]);

  useEffect(() => {
    if (meData) {
      setUser({
        id: meData.id,
        email: meData.email,
        name: meData.name ?? null,
        avatarUrl: meData.avatarUrl ?? null,
        systemRole: meData.systemRole === 'admin' ? 'admin' : 'user',
        onboardingCompleted: meData.onboardingCompleted ?? false,
      });
      // Sync UI language from the backend on login / page load. This ensures
      // a user who set English on one device gets English on another.
      const serverLang = meData.uiLanguage;
      if (serverLang && serverLang !== i18n.language) {
        i18n.changeLanguage(serverLang);
        localStorage.setItem('merkflow_ui_lang', serverLang);
      }
    }
  }, [meData, setUser, i18n]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <WorkspaceProvider>
      <AdminConfigProvider>
        {/* Kora layout model: the document NEVER scrolls. The outer flex row
          locks to viewport height (`h-screen overflow-hidden`) and only
          `<main>` is a scroll container. The previous model used document
          scroll with a `sticky top-0` topbar — that produced a "blank void"
          because content was scrolling underneath the topbar (a chart row
          could end up at `rectTop: 52` while the 60px header was sticky
          above it). Confining the scroll to `<main>` means the topbar and
          sidebar simply sit outside the scroll area and never overlap
          content. */}
        <div className="bg-page text-text-primary flex h-screen overflow-hidden">
          <Sidebar isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <TestEnvBanner />
            <AdminImpersonationBanner />
            <WorkspacePausedBanner />
            <ProviderStatusBanner />
            <Topbar
              onMobileMenuClick={() => setMobileNavOpen(true)}
              onChatToggle={() => setChatOpen((v) => !v)}
              chatOpen={chatOpen}
            />

            {/* The only scroll container in the dashboard. Topbar above,
              sidebar to the left, both static. Content below scrolls. */}
            <main className="flex-1 overflow-y-auto px-4 pt-6 pb-12 md:px-8 md:pt-6 md:pb-16">
              {/* Two-layer transition, Kora-style:
                - Outer motion.div (keyed by pathname) = cross-fade between
                  the outgoing and incoming route. Short, no Y — the Y
                  nudge on exit used to leave a blank scroll-slot gap.
                - Inner <PageEnter> = "page reveal" on mount. Fade + 20px
                  up-nudge, 0.3s. Remounts with the keyed parent, so it
                  runs on every navigation.
                Together: outgoing fades, incoming slides up into view. */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="mx-auto w-full max-w-7xl"
                >
                  <PageEnter>
                    <Outlet />
                  </PageEnter>
                </motion.div>
              </AnimatePresence>
            </main>
          </div>

          {/* V73 — 402 top-up picker. Mounted once globally; listens for
            `plan-limit-exceeded` custom events dispatched by the Axios
            interceptor and renders a pack picker when topupPacks are
            populated, plain upgrade CTA otherwise. */}
          <TopupPickerModal />

          {/* Global upgrade prompt for the 403 `feature_not_available` body
            shape AND for proactive `<UpgradeLockedWrapper>` clicks on
            gated CTAs. Both fire `feature-not-available` events. */}
          <FeatureNotAvailableModal />

          {/* V81 — one-time Pro notice for the AI Visibility flag flip.
            Suppresses automatically on non-Pro workspaces. */}
          <ProAiVisibilityNoticeMount />

          {/* V116 — Beta feedback widget. Self-hides when
            `beta_feedback_enabled = false`. Probes /api/feedback/config
            on mount; widget renders nothing while loading or if probe
            fails. NOT mounted on admin or public pages by design. */}
          <BetaFeedbackWidget />

          {/* V117 — Beta invite link redemption hook. Reads the code
            stashed in sessionStorage by /join/:code, calls redeem
            once the dashboard mounts (i.e. after onboarding has
            created the workspace). Idempotent — re-runs are no-ops
            via the UNIQUE constraint on (link, user). */}
          <BetaInviteRedemptionHook />

          {/* V152 — Merkflow AI chat panel. Portal-mounted slide-over;
            state lives in this layout so the panel persists across
            route changes. */}
          <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
        </div>
      </AdminConfigProvider>
    </WorkspaceProvider>
  );
}

/**
 * Wrapper that reads the active workspace + its BillingStatus and feeds
 * the Pro-only AI Visibility launch notice. Mounted inside
 * WorkspaceProvider so it can call useWorkspaceContext; BillingStatus
 * is fetched from useBillingStatus which reads workspace.plan +
 * planOverride (the V81 render conditions).
 */
function ProAiVisibilityNoticeMount() {
  const { activeWorkspace } = useWorkspaceContext();
  const { data: billing } = useBillingStatus(activeWorkspace?.id ?? '');
  if (!activeWorkspace) return null;
  return (
    <ProAiVisibilityLaunchedNotice
      workspacePlan={billing?.plan}
      planOverride={billing?.planOverride}
    />
  );
}

/**
 * Sticky banner shown when a platform admin opened a customer workspace
 * via /admin/workspaces. The "Exit" button drops the impersonation flag
 * and snaps back to the admin's first own workspace.
 */
function AdminImpersonationBanner() {
  const { isImpersonating, activeWorkspace, stopImpersonating } = useWorkspaceContext();
  if (!isImpersonating || !activeWorkspace) return null;
  return (
    <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-red-500/40 bg-red-500/15 px-4 py-1.5 text-xs text-red-300">
      <span>
        <span className="font-semibold">Platform admin:</span> viewing{' '}
        <span className="font-mono">{activeWorkspace.name}</span> with full owner access. Any action
        you take writes to the customer's data.
      </span>
      <button
        type="button"
        onClick={stopImpersonating}
        className="rounded-md border border-red-500/40 px-2 py-0.5 font-semibold tracking-wider text-red-200 uppercase hover:bg-red-500/20"
      >
        Exit
      </button>
    </div>
  );
}

/**
 * Test-environment banner — shown on every dashboard page when
 * `/api/admin/config.mockEnabled === true` (i.e. test.merkflow.eu).
 * Nothing shows on prod or local dev. Single short line so it doesn't
 * steal real estate; orange accent so it's distinct from product UI.
 */
function TestEnvBanner() {
  const { mockEnabled } = useAdminConfig();
  if (!mockEnabled) return null;
  return (
    <div className="flex-shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-center text-xs text-amber-400">
      🧪 Test environment — all social integrations are mocked
    </div>
  );
}

/**
 * V117 — Redeems a beta-invite code once the user lands on the
 * dashboard. Two sources for the code, in priority order:
 *
 *   1. `sessionStorage.beta_invite_code` set by /join/:code at landing
 *      time. Works for OAuth signups in the same tab.
 *   2. `me.signupInviteCode` from `/api/auth/me` (V151 column stamped
 *      by AuthController.register). Survives the verify-email
 *      round-trip and cross-device signup that wipes sessionStorage —
 *      the exact path info@merkflow.eu hit, where the workspace stayed
 *      on plan="trial" because the hook had nothing to redeem.
 *
 * By this point the workspace has been created (OAuth2SuccessHandler.
 * ensureWorkspaceExists or the onboarding wizard), so the redemption
 * can flip workspace.plan + plan_override cleanly. Idempotent: re-fires
 * are no-ops because the redemption row's UNIQUE constraint blocks
 * double-counting.
 *
 * Renders nothing — pure side effect.
 */
function BetaInviteRedemptionHook() {
  const { data: me } = useMe();
  useEffect(() => {
    const code = sessionStorage.getItem('beta_invite_code') ?? me?.signupInviteCode ?? null;
    if (!code) return;
    inviteLinkApi
      .redeem(code)
      .then(() => sessionStorage.removeItem('beta_invite_code'))
      .catch(() => {
        // Failure is best-effort. Leave the code in storage so the
        // next dashboard mount retries. V151 marker stays put on
        // failure too — the next mount will retry from there.
      });
  }, [me?.signupInviteCode]);
  return null;
}
